import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { assetUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Download, AlertTriangle, Clock, Users, MapPin } from "lucide-react";
import { computeServiceTimes } from "@/lib/serviceTime";
import { formatDistance } from "@/lib/geolocation";
import { LocationMapDialog } from "@/components/LocationMapDialog";

export default function AdminReports() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [techs, setTechs] = useState([]);
    const [clients, setClients] = useState([]);
    const [tech, setTech] = useState("all");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [mispricedOnly, setMispricedOnly] = useState(searchParams.get("mispriced") === "1");
    const [locationOpen, setLocationOpen] = useState(false);
    const [locationData, setLocationData] = useState(null);

    useEffect(() => {
        api.get("/technicians").then((r) => setTechs(r.data));
        api.get("/clients").then((r) => setClients(r.data));
    }, []);

    useEffect(() => {
        const params = {};
        if (tech && tech !== "all") params.technician_id = tech;
        if (from) params.date_from = from;
        if (to) params.date_to = to;
        if (mispricedOnly) params.mispriced_only = true;
        api.get("/reports", { params }).then((r) => setItems(r.data));
    }, [tech, from, to, mispricedOnly]);

    // Sync mispriced filter with URL
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (mispricedOnly) next.set("mispriced", "1");
        else next.delete("mispriced");
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mispricedOnly]);

    return (
        <div data-testid="admin-reports">
            <h1 className="text-2xl font-semibold mb-4" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Reportes</h1>
            <div className="flex gap-3 flex-wrap mb-3 items-end">
                <div><label className="text-xs text-muted-foreground">Desde</label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="admin-reports-from" /></div>
                <div><label className="text-xs text-muted-foreground">Hasta</label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="admin-reports-to" /></div>
                <div>
                    <label className="text-xs text-muted-foreground">Técnico</label>
                    <Select value={tech} onValueChange={setTech}>
                        <SelectTrigger className="w-56" data-testid="admin-reports-tech"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            {techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.code} – {t.full_name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className={`flex items-center gap-2 h-10 px-3 rounded-md border ${mispricedOnly ? "border-[#FF0000]/50 bg-[#FF0000]/5" : ""}`}>
                    <Switch
                        checked={mispricedOnly}
                        onCheckedChange={setMispricedOnly}
                        className="data-[state=checked]:bg-[#FF0000] data-[state=unchecked]:bg-zinc-300"
                        data-testid="admin-reports-mispriced-filter"
                        id="mispriced-only"
                    />
                    <Label htmlFor="mispriced-only" className={`text-sm cursor-pointer ${mispricedOnly ? "text-[#FF0000] font-semibold" : ""}`}>
                        Solo alertas de precio
                    </Label>
                </div>
            </div>
            <Card data-testid="admin-reports-table">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Fecha</TableHead>
                            <TableHead>Cliente</TableHead>
                            <TableHead>Técnico</TableHead>
                            <TableHead className="text-center">Duración</TableHead>
                            <TableHead className="text-center">Horas hombre</TableHead>
                            <TableHead className="text-center">Ubicación inicio</TableHead>
                            <TableHead>Factura</TableHead>
                            <TableHead>Pago</TableHead>
                            <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">Sin reportes</TableCell></TableRow>}
                        {items.map((r) => {
                            const c = clients.find((x) => x.id === r.client_id);
                            const t = techs.find((x) => x.id === r.technician_id);
                            const supportCount = (r.support_technician_ids || []).length;
                            const times = computeServiceTimes(r.entry_time, r.exit_time, supportCount);
                            return (
                                <TableRow
                                    key={r.id}
                                    className={r.service_mispriced ? "bg-[#FF0000]/[0.04] hover:bg-[#FF0000]/[0.07]" : undefined}
                                    data-testid={`admin-reports-row-${r.id}`}
                                >
                                    <TableCell className="font-mono">{r.service_date}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <span>{c?.name || "—"}</span>
                                            {r.service_mispriced && (
                                                <Badge
                                                    className="bg-[#FF0000] text-white hover:bg-[#FF0000] gap-1"
                                                    data-testid={`admin-reports-mispriced-badge-${r.id}`}
                                                    title="El técnico marcó este servicio como mal cobrado"
                                                >
                                                    <AlertTriangle className="w-3 h-3" />
                                                    Mal cobrado
                                                </Badge>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1.5">
                                            <span>{t?.full_name || "—"}</span>
                                            {supportCount > 0 && (
                                                <Badge variant="outline" className="font-mono text-[10px]" title={`+${supportCount} técnico(s) de apoyo`}>
                                                    <Users className="w-3 h-3 mr-0.5" /> +{supportCount}
                                                </Badge>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-center font-mono text-sm">
                                        {times.durationMinutes !== null ? (
                                            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3 text-muted-foreground" />{times.durationLabel}</span>
                                        ) : (
                                            <span className="text-muted-foreground">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-center font-mono text-sm">
                                        {times.manHours !== null ? (
                                            <span className="inline-flex items-center gap-1 font-semibold">
                                                <Users className="w-3 h-3 text-muted-foreground" />{times.manHoursLabel}
                                            </span>
                                        ) : (
                                            <span className="text-muted-foreground">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-center text-xs">
                                        {r.start_latitude != null && r.start_longitude != null ? (() => {
                                            const dist = r.start_distance_to_client_m;
                                            const distFar = dist != null && dist > 200;
                                            return (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setLocationData({
                                                            techLat: r.start_latitude,
                                                            techLng: r.start_longitude,
                                                            accuracyM: r.start_accuracy_m,
                                                            addressResolved: r.start_address_resolved,
                                                            clientLat: c?.geocoded_latitude ?? null,
                                                            clientLng: c?.geocoded_longitude ?? null,
                                                            clientName: c?.name,
                                                            clientAddress: c?.address,
                                                            distanceM: r.start_distance_to_client_m,
                                                            capturedAt: r.entry_time,
                                                        });
                                                        setLocationOpen(true);
                                                    }}
                                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-md transition-colors ${distFar ? "bg-[#FF0000]/10 text-[#FF0000] hover:bg-[#FF0000]/20" : "bg-green-50 text-green-800 hover:bg-green-100"}`}
                                                    data-testid={`admin-reports-loc-${r.id}`}
                                                >
                                                    <MapPin className="w-3 h-3" />
                                                    {dist != null ? formatDistance(dist) : "Ver"}
                                                </button>
                                            );
                                        })() : (
                                            <span className="text-muted-foreground italic">No registró</span>
                                        )}
                                    </TableCell>
                                    <TableCell><Badge variant="outline" className="font-mono">{r.dte_invoice_number}</Badge></TableCell>
                                    <TableCell className="uppercase text-xs text-muted-foreground">{r.payment_type || "—"}</TableCell>
                                    <TableCell className="text-right space-x-1">
                                        <a href={assetUrl(r.report_pdf_url)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Download className="w-3.5 h-3.5 mr-1" />PDF</Button></a>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Card>

            {locationData && (
                <LocationMapDialog
                    open={locationOpen}
                    onOpenChange={(o) => { setLocationOpen(o); if (!o) setLocationData(null); }}
                    {...locationData}
                />
            )}
        </div>
    );
}
